import type { GetTicketByIdResponse } from "shared/types/serviceDesk";
import { resolveChatVariable } from "./chatVariableOptions";

/**
 * Resolves the variable chips inside a canned-note HTML string to their
 * literal values against the current ticket, and strips chip wrappers.
 *
 * Canned notes are authored in the email editor's TipTapChipEditor which
 * serializes variable chips as:
 *   <span data-type="variable-chip" class="dd-template-chip"
 *         data-id="..." data-label="..." ...>...</span>
 *
 * We replace each chip span with a plain text node containing the resolved
 * literal value. A secondary pass also handles any stray `{{id}}` plain-text
 * placeholders for resilience.
 *
 * Returns an empty string when the input is falsy. Never throws.
 */
export const resolveCannedNoteHtml = (
  html: string | null | undefined,
  ticket: GetTicketByIdResponse | null | undefined,
): string => {
  if (!html) return "";

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");

    const chipSelector = [
      "span[data-type='variable-chip']",
      "span.dd-template-chip",
    ].join(", ");

    doc.querySelectorAll(chipSelector).forEach((el) => {
      const id =
        el.getAttribute("data-id") ||
        el.getAttribute("data-chip-id") ||
        "";
      const resolved = resolveChatVariable(id, ticket);
      el.replaceWith(doc.createTextNode(resolved));
    });

    const walk = (node: Node): void => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent) {
        node.textContent = node.textContent.replace(
          /\{\{(\w[\w-]*)\}\}/g,
          (_match, id: string) => resolveChatVariable(id, ticket) || "",
        );
      } else {
        node.childNodes.forEach(walk);
      }
    };
    walk(doc.body);

    return doc.body.innerHTML;
  } catch {
    return html;
  }
};
